import { useCallback, useEffect, useState } from 'react';
import waApi from '../../services/whatsapp/waApi';

// ─── Contrato do backend (#64) ──────────────────────────────────────────────
// Insights automáticos DETERMINÍSTICOS: derivados das métricas reais (SLA,
// agentes, volume, clientes), sem IA. Cada item já vem pronto para virar card.
export type AutoInsightSeveridade = 'info' | 'warning' | 'critical';

export interface AutoInsight {
  key: string;
  titulo: string;
  valor: string;
  detalhe: string;
  severidade: AutoInsightSeveridade;
}

export interface AutoInsightsResponse {
  insights: AutoInsight[];
  generated_at: string;
}

// 'sem_permissao' quando o backend devolve 403 (endpoint exige admin/supervisor),
// igual ao padrão de useWaDashboardMetrics. 'erro' para qualquer outra falha.
type AutoInsightsError = 'sem_permissao' | 'erro' | null;

function mapError(err: any): AutoInsightsError {
  return err?.response?.status === 403 ? 'sem_permissao' : 'erro';
}

/**
 * Insights automáticos do dashboard — GET /dashboard/auto-insights
 * (admin/supervisor). Mesmo padrão dos demais hooks de dashboard do WhatsApp:
 * estados loading/error/403 e refetch manual. Usa waApi (whatsapp-api), não o
 * api principal.
 */
export function useWaAutoInsights(days = 30) {
  const [data, setData] = useState<AutoInsight[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<AutoInsightsError>(null);

  const fetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await waApi.get<AutoInsightsResponse>('/dashboard/auto-insights', {
        params: { days },
      });
      setData(Array.isArray(res.data?.insights) ? res.data.insights : []);
    } catch (err) {
      setError(mapError(err));
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, isLoading, error, refetch: fetch };
}

export default useWaAutoInsights;
